import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import s from '../../../../../redux/selectors';
import { wrap, withT } from '../../../../../lib/wrappers';
import InlineInput from '../InlineInput';
import InlineSelect from '../InlineSelect';
import BlankLink from '../../../../common/BlankLink';

const fridgeOptions = t => [
  { value: 0, display: t('Manual Defrost Refrigerators') },
  { value: 1, display: t('Partial Automatic Defrost Refrigerators') },
  { value: 2, display: t('Top Mount Freezer without through-the-door ice') },
  { value: 3, display: t('Side Mount Freezer without through-the-door ice') },
  { value: 4, display: t('Bottom Mount Freezer without through-the-door ice') },
  { value: 5, display: t('Top Mount Freezer with through-the-door ice') },
  { value: 6, display: t('Side Mount Freezer with through-the-door ice') },
];

const ChooseEnergyStarFridge = ({
  t,
  isMetric,
  widgetSource,
  resultTakeactionEnergyStarFridgeTotvolume,
  resultTakeactionEnergyStarFridgeFridgekwh,
}) => (
  <div className="action-details">
    <div className="action-details__form">
      <p>
        {t('The next time I purchase a new refrigerator, I pledge to make it an Energy Star qualified decision. New refrigerator type')}
        <InlineSelect
          options={fridgeOptions(t)}
          apiKey={'input_takeaction_energy_star_fridge_fridgetype'}
        />
        {t('. The Energy Star model costs an additional')}
        <InlineInput
          currency
          maxLength={5}
          apiKey={'input_takeaction_energy_star_fridge_fridgecost'}
        />
        {t('dollars compared to the conventional model.')}
      </p>
    </div>
    <div className="action-details__links">
      {widgetSource !== 'ontario' && <a
        href={'"Eat up" less energy: A new refrigerator with an ENERGY STAR label beats current federal standards by at least 20%.'}
        target="_blank"
        rel="noopener noreferrer"
      >{t('Find rebates and incentives for this action')}</a>}
      {widgetSource === 'ontario' &&
        <div>
          <p>
            {t('You may benefit from a free program that helps low-income customers reduce their energy use at home, with a free energy assessment and upgrades. Energy saving devices such as ENERGY STAR® refrigerators and freezers will be installed free of charge. To learn more about this program, check out the')}
            &nbsp;
            <BlankLink href={`https://${t('greenon.ca/')}`} text={t('Green Ontario Fund Website')} />.
          </p>
          <p>
            {t('You can take advantage of rebates that are available for home upgrades. As part of the Home Energy Conservation Program, you can take advantage of rebates of $75 for the purchase of an energy efficient fridge. To learn more about this program, check out the')}
            &nbsp;
            <BlankLink href={`https://${t('greenon.ca/')}`} text={t('Green Ontario Fund Website')} />.
          </p>
          <p>
            {t('If you live in a First Nations Community, you are eligible to receive free energy-efficient upgrades for products designed to add comfort and increase efficiency. This includes items such as LED light bulbs, powerbars with digital timers, programmable thermostats and ENERGY STAR® certified appliances (including fridges and freezers). If your home is electrically heated, you are also eligible for free attic and basement insulation. To learn more about this program, check out the')}
            &nbsp;
            <BlankLink href={`https://${t('greenon.ca/')}`} text={t('Green Ontario Fund Website')} />.
          </p>
        </div>
      }
    </div>
    <div className="action-details__assumptions">
      <div className="action-details__assumptions__header">{t('Assumptions')}</div>
      <ul>
        <li>
          {t('Refrigerator fresh volume:')}
          <InlineInput
            maxLength={3}
            apiKey="input_takeaction_energy_star_fridge_fresh"
          />
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: `${isMetric ? t('m', { ns: 'units' }) : t('ft', { ns: 'units' })}<sup>3</sup>`,
            }}
          />
        </li>
        <li>
          {t('Refrigerator freezer volume:')}
          <InlineInput
            maxLength={3}
            apiKey="input_takeaction_energy_star_fridge_freezer"
          />
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: `${isMetric ? t('m', { ns: 'units' }) : t('ft', { ns: 'units' })}<sup>3</sup>`,
            }}
          />
        </li>
        <li
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: t('The total volume is: {{resultTakeactionEnergyStarFridgeTotvolume}} {{volumeUnits}}', {
              resultTakeactionEnergyStarFridgeTotvolume,
              volumeUnits: `${isMetric ? t('m', { ns: 'units' }) : t('ft', { ns: 'units' })}<sup>3</sup>`,
            }),
          }}
        />
        <li
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: t('Efficiency of Energy Star model: {{resultTakeactionEnergyStarFridgeFridgekwh}} kWh/ {{volumeUnits}}<sup>3</sup>', {
              resultTakeactionEnergyStarFridgeFridgekwh,
              volumeUnits: isMetric ? t('m', { ns: 'units' }) : t('ft', { ns: 'units' }),
            }),
          }}
        />
        <li>
          {t('cost of electricity')}
          <InlineInput
            apiKey="input_takeaction_assumption_kwhprice"
            maxLength={3}
          />
          {t('cents per kWh')}
        </li>
      </ul>
    </div>
  </div>
);

ChooseEnergyStarFridge.propTypes = {
  // wrapped
  t: PropTypes.func.isRequired,
  // connected props
  isMetric: PropTypes.bool.isRequired,
  widgetSource: PropTypes.string.isRequired,
  resultTakeactionEnergyStarFridgeTotvolume: PropTypes.string.isRequired,
  resultTakeactionEnergyStarFridgeFridgekwh: PropTypes.string.isRequired,
};

const mapStateToProps = (state, ownProps) => [
  'resultTakeactionEnergyStarFridgeTotvolume',
  'resultTakeactionEnergyStarFridgeFridgekwh',
].reduce((resultProps, resultKey) => ({
  ...resultProps,
  [resultKey]: s.results(resultKey)(state, ownProps),
}), {
  isMetric: s.isMetric(state),
  widgetSource: s.widgetSource(state),
});

const mapDispatchToProps = () => ({});

export default wrap(
  withT('energyStarFridge'),
  connect(mapStateToProps, mapDispatchToProps)(ChooseEnergyStarFridge),
);
